/**
 * Gestionnaire du sélecteur de vidéo
 */
export class VideoSelector {
    constructor(videoSourceManager) {
        this.videoSourceManager = videoSourceManager;
        this.isDropdownOpen = false;
        this.currentSourceType = 'webcam'; // 'webcam', 'file', 'url'
        
        this.init();
    }

    /**
     * Initialise le sélecteur de vidéo
     */
    init() {
        this.button = document.getElementById('videoSelectorButton');
        this.dropdown = document.getElementById('videoDropdown');
        
        if (!this.button || !this.dropdown) {
            console.warn('⚠️ Éléments du sélecteur de vidéo non trouvés');
            return;
        }

        // Event listeners
        this.button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleDropdown();
        });

        // Fermer le dropdown en cliquant ailleurs
        document.addEventListener('click', (e) => {
            if (!e.target.closest('#videoSelector')) {
                this.closeDropdown();
            }
        });

        // Ajouter les event listeners pour les options
        this.setupOptionListeners();
        
        // Initialiser avec l'état actuel du VideoSourceManager
        const currentSourceType = this.videoSourceManager.getCurrentVideoSourceType();
        this.currentSourceType = currentSourceType;
        this.updateActiveOption(currentSourceType);
        this.updateButtonIcon(currentSourceType);
    }

    /**
     * Configure les event listeners pour les options du dropdown
     */
    setupOptionListeners() {
        const options = this.dropdown.querySelectorAll('.video-option');
        
        options.forEach(option => {
            option.addEventListener('click', () => {
                const action = option.dataset.action;
                this.handleOptionClick(action);
            });
        });
    }

    /**
     * Gère le clic sur une option du dropdown
     */
    async handleOptionClick(action) {
        try {
            // console.log(`🎬 Action sélectionnée: ${action}`);
            
            switch (action) {
                case 'file':
                    await this.selectVideoFile();
                    break;
                case 'url':
                    await this.selectVideoUrl();
                    break;
                case 'webcam':
                    await this.selectWebcam();
                    break;
                default:
                    console.warn('Action non reconnue:', action);
                    return;
            }
            
            // Mettre à jour l'état et fermer le dropdown
            this.currentSourceType = action;
            this.updateActiveOption(action);
            this.updateButtonIcon(action);
            this.closeDropdown();
            
        } catch (error) {
            console.error('Erreur lors de la sélection:', error);
        }
    }

    /**
     * Sélectionne un fichier vidéo local
     */
    async selectVideoFile() {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'video/*';
            
            input.onchange = async (e) => {
                const file = e.target.files[0];
                if (file) {
                    try {
                        // console.log(`🎬 Chargement du fichier: ${file.name}`);
                        await this.videoSourceManager.loadVideoFile(file);
                        resolve();
                    } catch (error) {
                        console.error('Erreur lors du chargement du fichier:', error);
                        reject(error);
                    }
                } else {
                    reject(new Error('Aucun fichier sélectionné'));
                }
            };
            
            input.oncancel = () => {
                reject(new Error('Sélection annulée'));
            };
            
            input.click();
        });
    }

    /**
     * Sélectionne une URL vidéo
     */
    async selectVideoUrl() {
        const url = prompt('Entrez l\'URL de la vidéo:', 'https://');
        
        if (url && url.trim() && url !== 'https://') {
            try {
                console.log(`🎬 Chargement de l'URL: ${url}`);
                await this.videoSourceManager.loadVideoUrl(url.trim());
            } catch (error) {
                console.error('Erreur lors du chargement de l\'URL:', error);
                alert('Error loading video. Please check the URL.');
                throw error;
            }
        } else {
            throw new Error('URL non valide ou vide');
        }
    }

    /**
     * Retourne à la webcam
     */
    async selectWebcam() {
        try {
            console.log('🎬 Retour à la webcam');
            await this.videoSourceManager.startWebcam();
        } catch (error) {
            console.error('Erreur lors du retour à la webcam:', error);
            throw error;
        }
    }

    /**
     * Met à jour l'option active dans le dropdown
     */
    updateActiveOption(action) {
        // Retirer la classe active de toutes les options
        this.dropdown.querySelectorAll('.video-option').forEach(option => {
            option.classList.remove('active');
        });

        // Ajouter la classe active à l'option sélectionnée
        const activeOption = this.dropdown.querySelector(`[data-action="${action}"]`);
        if (activeOption) {
            activeOption.classList.add('active');
        }
    }

    /**
     * Met à jour l'icône du bouton selon le type de source
     */
    updateButtonIcon(sourceType) {
        // L'icône reste toujours un dossier
        this.button.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>';
    }

    /**
     * Bascule l'affichage du dropdown
     */
    toggleDropdown() {
        if (this.isDropdownOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    /**
     * Ouvre le dropdown
     */
    openDropdown() {
        this.dropdown.classList.add('show');
        this.isDropdownOpen = true;
        
        // Changer l'apparence du bouton
        this.button.style.background = 'rgba(0, 0, 0, 0.9)';
        this.button.style.borderColor = 'rgba(255, 255, 255, 0.6)';
    }

    /**
     * Ferme le dropdown
     */
    closeDropdown() {
        this.dropdown.classList.remove('show');
        this.isDropdownOpen = false;
        
        // Restaurer l'apparence du bouton
        this.button.style.background = 'rgba(0, 0, 0, 0.7)';
        this.button.style.borderColor = 'rgba(255, 255, 255, 0.3)';
    }

    /**
     * Met à jour l'état du sélecteur selon la source vidéo actuelle
     */
    updateVideoSourceInfo(sourceType) {
        this.currentSourceType = sourceType;
        this.updateActiveOption(sourceType);
        this.updateButtonIcon(sourceType);
    }

    /**
     * Rafraîchit le sélecteur
     */
    refresh() {
        // Mettre à jour l'affichage selon l'état actuel
        this.updateActiveOption(this.currentSourceType);
        this.updateButtonIcon(this.currentSourceType);
    }
}
